import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { SubsWithJoin } from "./subs.entity";
import * as subsApi from "./subs.api";
import type { SubcontractSortKey, SubcontractStatus } from "@/repositories/subs.repository";

interface SubsState {
  subs: SubsWithJoin[];
  loading: boolean;
  total: number;
  filters: {
    page: number;
    pageSize: number;
    name: string;
    service: string;
    sortKey: SubcontractSortKey;
    sortOrder: "asc" | "desc";
    status: SubcontractStatus;
  };
  inviteSaving: boolean;
  inviteOpen: boolean;
  inviteError: string | null;
}

const initialState: SubsState = {
  subs: [],
  loading: false,
  total: 0,
  filters: {
    page: 1,
    pageSize: 10,
    name: "",
    service: "",
    sortKey: "name",
    sortOrder: "asc",
    status: "live",
  },
  inviteSaving: false,
  inviteOpen: false,
  inviteError: null,
};

export const fetchSubs = createAsyncThunk("subs/fetchSubs", async (_, { getState }) => {
  const state = getState() as { subs: SubsState };
  const { filters } = state.subs;

  return subsApi.searchSubs(
    filters.page,
    filters.pageSize,
    filters.name || undefined,
    filters.service || undefined,
    filters.sortKey,
    filters.sortOrder,
    filters.status
  );
});

export const sendSubsInvite = createAsyncThunk("subs/sendSubsInvite", async (email: string) => {
  return subsApi.inviteSubs(email);
});

export const removeSubs = createAsyncThunk("subs/removeSubs", async (subsId: string) => {
  return subsApi.removeSubsFromOrg(subsId);
});

const subsSlice = createSlice({
  name: "subs",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filters.pageSize = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.filters.name = action.payload;
    },
    setService: (state, action: PayloadAction<string>) => {
      state.filters.service = action.payload;
    },
    setSortKey: (state, action: PayloadAction<SubcontractSortKey>) => {
      state.filters.sortKey = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.filters.sortOrder = action.payload;
    },
    setStatus: (state, action: PayloadAction<SubcontractStatus>) => {
      state.filters.status = action.payload;
      state.filters.page = 1;
    },
    setInviteOpen: (state, action: PayloadAction<boolean>) => {
      state.inviteOpen = action.payload;
      if (!action.payload) {
        state.inviteError = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubs.fulfilled, (state, action) => {
        state.loading = false;
        state.subs = action.payload.data || [];
        state.total = action.payload.count || 0;
      })
      .addCase(fetchSubs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(sendSubsInvite.pending, (state) => {
        state.inviteSaving = true;
        state.inviteError = null;
      })
      .addCase(sendSubsInvite.fulfilled, (state) => {
        state.inviteSaving = false;
        state.inviteOpen = false;
      })
      .addCase(sendSubsInvite.rejected, (state, action) => {
        state.inviteSaving = false;
        state.inviteError = action.error.message || "Failed to send invite";
      });
  },
});

export const { setPage, setPageSize, setName, setService, setSortKey, setSortOrder, setStatus, setInviteOpen } = subsSlice.actions;

export default subsSlice.reducer;
